import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, FileText, ListChecks, Megaphone, Lock } from "lucide-react";
import api, { apiError } from "../lib/api";
import { UploadBtn } from "./UploadBtn";

const TYPES = [
  { key: "update", label: "Update", Icon: Megaphone, feat: null },
  { key: "notes", label: "Notes", Icon: FileText, feat: "notes" },
  { key: "mcq", label: "MCQ Test", Icon: ListChecks, feat: "mcq" },
];
const emptyQ = () => ({ q: "", options: ["", "", "", ""], answer: 0 });

export default function PostComposer({ features, postsToday, onDone }) {
  const [type, setType] = useState("update");
  const [form, setForm] = useState({ title: "", content: "", link: "", file_url: "" });
  const [questions, setQuestions] = useState([emptyQ()]);
  const limit = Number(features?.posts_per_day || 0);
  const remaining = Math.max(0, limit - (postsToday || 0));

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, type, questions: type === "mcq" ? questions : null };
      const r = await api.post("/institute/posts", body);
      toast.success(r.data.message);
      setForm({ title: "", content: "", link: "", file_url: "" }); setQuestions([emptyQ()]);
      onDone?.();
    } catch (err) { toast.error(apiError(err)); }
  };
  const setQ = (i, patch) => setQuestions(questions.map((q, j) => (j === i ? { ...q, ...patch } : q)));

  if (limit <= 0) {
    return (
      <div data-testid="posts-locked" className="mt-4 nb-flat p-5 text-center border-dashed text-sm text-slate-600" style={{ borderRadius: 12 }}>
        <Lock className="w-5 h-5 mx-auto mb-2 text-slate-400" /> Posting is available on paid plans. Upgrade to publish updates, notes and MCQ tests.
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2">
          {TYPES.map(({ key, label, Icon, feat }) => {
            const locked = feat && !features?.[feat];
            return (
              <button key={key} type="button" data-testid={`post-type-${key}`} disabled={locked} onClick={() => setType(key)}
                className="nb-btn nb-btn-sm" style={{ background: type === key ? "#A3E635" : "#fff", opacity: locked ? 0.45 : 1 }}>
                <Icon className="w-4 h-4" /> {label}{locked && <Lock className="w-3 h-3" />}
              </button>
            );
          })}
        </div>
        <span data-testid="posts-remaining" className="nb-badge" style={{ background: remaining ? "#F2FBE3" : "#FCA5A5" }}>{remaining}/{limit} posts left today</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><label className="nb-label">Title</label>
          <input data-testid="post-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="nb-input mt-1.5" placeholder={type === "mcq" ? "Physics Mock Test 1" : type === "notes" ? "Chapter 3 — Thermodynamics notes" : "Admission test on 15 July"} /></div>
        <div><label className="nb-label">Link (optional)</label>
          <input data-testid="post-link-input" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} className="nb-input mt-1.5" placeholder="https://..." /></div>
        <div className="sm:col-span-2"><label className="nb-label">{type === "mcq" ? "Instructions" : "Details"}</label>
          <textarea data-testid="post-content-input" rows={2} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="nb-input mt-1.5" /></div>
        {type === "notes" && (
          <div className="sm:col-span-2 flex gap-2 items-center">
            <input data-testid="post-file-input" value={form.file_url} onChange={(e) => setForm({ ...form, file_url: e.target.value })} className="nb-input flex-1 min-w-0" placeholder="Notes file/image URL or upload →" />
            <UploadBtn testId="upload-notes-file" onDone={(url) => setForm((f) => ({ ...f, file_url: url }))} />
          </div>
        )}
      </div>
      {type === "mcq" && (
        <div className="space-y-3">
          {questions.map((q, i) => (
            <div key={i} data-testid={`mcq-question-${i}`} className="nb-flat p-3 space-y-2" style={{ borderRadius: 12 }}>
              <div className="flex gap-2">
                <input data-testid={`mcq-q-${i}`} required value={q.q} onChange={(e) => setQ(i, { q: e.target.value })} className="nb-input flex-1" placeholder={`Question ${i + 1}`} />
                {questions.length > 1 && <button type="button" onClick={() => setQuestions(questions.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {q.options.map((o, k) => (
                  <label key={k} className="flex items-center gap-2 text-sm">
                    <input type="radio" name={`ans-${i}`} checked={q.answer === k} onChange={() => setQ(i, { answer: k })} data-testid={`mcq-ans-${i}-${k}`} />
                    <input data-testid={`mcq-opt-${i}-${k}`} required value={o} onChange={(e) => setQ(i, { options: q.options.map((x, m) => (m === k ? e.target.value : x)) })} className="nb-input" placeholder={`Option ${k + 1}`} />
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-slate-500">Select the radio next to the correct option.</p>
            </div>
          ))}
          <button type="button" data-testid="mcq-add-question" onClick={() => setQuestions([...questions, emptyQ()])} className="nb-btn nb-btn-sm"><Plus className="w-4 h-4" /> Add question</button>
        </div>
      )}
      <button data-testid="post-publish-btn" disabled={!remaining} className="nb-btn nb-btn-lime">Publish {TYPES.find((t) => t.key === type).label}</button>
    </form>
  );
}
